<template>
	<div
		:class="['tooltip-container', { 'tooltip-error': isError }]"
		@mouseenter="calcPosition()"
		ref="container"
	>
		<div
			v-if="text"
			class="caption"
			@click="handleAction()"
		>
			{{ text }}
		</div>

		<div class="tooltip-outside">
			<slot name="showFromOutside"></slot>
		</div>

		<div
			v-if="showTooltip && tooltip?.length > 0"
			ref="tooltipRef"
			:class="pointerClass"
		>
			<div
				v-for="(tip, i) in tooltip"
				:key="i"
				class="wrap-text"
				:class="customClass"
			>
				{{ tip }}
				<div
					v-if="!tip"
					class="tooltip-gap"
				></div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export interface ITooltipProps {
		/** The collection of strings to display*/
		tooltip: string | string[];
		text?: string;
		direction?: string;
		action?: any;
		maxWidth?: number | string; // in pixels
		tooltipOffset?: number | string; // in pixels
		customClass?: string;
		isError?: boolean | false;
	}
</script>

<script setup lang="ts">
	import { computed, onMounted, ref, toRefs } from 'vue';
	import util from '@core/services/util';

	const props = defineProps<ITooltipProps>();
	const { tooltip, action, direction, maxWidth, tooltipOffset, isError } = toRefs(props);

	const dir = ref<string>(null);

	const tooltipRef = ref(null);
	const container = ref(null);

	const showTooltip = computed(() => {
		if (tooltip.value == null) return false;
		return tooltip.value.length > 0;
	});

	const pointerClass = computed(() => `tooltip pointer-${dir.value}`);

	init();

	onMounted(() => {
		if (tooltipRef.value) {
			if (util.isNull(maxWidth.value)) tooltipRef.value.style.whiteSpace = 'nowrap';
			else tooltipRef.value.style.maxWidth = maxWidth.value + 'px';
		}
	});

	function init() {
		dir.value = util.isNull(direction.value) ? 'left' : direction.value.toLowerCase();

		if (tooltipRef.value) {
			if (util.isNull(maxWidth.value)) tooltipRef.value.style.whiteSpace = 'nowrap';
			else tooltipRef.value.style.maxWidth = maxWidth.value + 'px';
		}
	}

	function handleAction() {
		if (util.isNull(action.value) || typeof action.value !== 'object' || typeof action.value.function !== 'function') return;

		if (action.value.param.length <= 0) {
			action.value.function();
		} else {
			action.value.function(action.value.param);
		}
	}

	function calcPosition() {
		if (!showTooltip.value || !tooltipRef.value || !container.value) return;

		const targetRect = container.value.getBoundingClientRect();
		const tooltipRect = tooltipRef.value.getBoundingClientRect();

		let top, left;
		switch (dir.value) {
			case 'top':
				top = `${targetRect.top - tooltipRect.height - (Number(tooltipOffset.value) || 15)}px`;
				left = `${targetRect.left + targetRect.width / 2 - tooltipRect.width / 2}px`;
				break;
			case 'right':
				top = `${targetRect.top + targetRect.height / 2 - tooltipRect.height / 2}px`;
				left = `${targetRect.left + targetRect.width + (Number(tooltipOffset.value) || 20)}px`;
				break;
			case 'bottom':
				top = `${targetRect.top + targetRect.height + (Number(tooltipOffset.value) || 15)}px`;
				left = `${targetRect.left + targetRect.width / 2 - tooltipRect.width / 2}px`;
				break;
			case 'left':
			default:
				top = `${targetRect.top + targetRect.height / 2 - tooltipRect.height / 2}px`;
				left = `${targetRect.left - tooltipRect.width - (Number(tooltipOffset.value) || 20)}px`;
				break;
		}

		tooltipRef.value.style.top = top;
		tooltipRef.value.style.left = left;
	}
</script>
<style>
	.tooltip-outside {
		width: 100%;
	}

	.wrap-text {
		overflow-wrap: break-word;
		text-wrap: auto;
	}
</style>
